import Button from "@mui/material/Button";
import type { SystemSettingsForm } from "../../SystemSettings.hooks";
import { type FieldEntry, schemaDefaultsToApply } from "../../SystemSettings.utils";
import SystemSettingsFooterReset from "./components/SystemSettingsFooterReset/SystemSettingsFooterReset";

export interface SystemSettingsTestAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  // PostHog action name.
  trackingAction: string;
}

interface SystemSettingsFooterProps {
  // A section's "test it" button (send a test email, test the connection), if any.
  testAction?: SystemSettingsTestAction;
  form: SystemSettingsForm;
  // Present on schema sections. Resets every visible setting, not just this section.
  resetFields?: FieldEntry[];
}

export default function SystemSettingsFooter({
  testAction,
  form,
  resetFields,
}: SystemSettingsFooterProps) {
  return (
    <footer className="flex justify-between gap-4 p-6">
      {testAction ? (
        <Button
          variant="outlined"
          onClick={testAction.onClick}
          disabled={testAction.isLoading}
          data-ph-capture-attribute-action={testAction.trackingAction}
        >
          {testAction.label}
        </Button>
      ) : (
        <span />
      )}
      <form.Subscribe
        // isDefaultValue, not isPristine: reverting an edit makes the form clean again.
        selector={(state) => {
          const changeCount = Object.values(state.fieldMeta).filter(
            (meta) => meta && !meta.isDefaultValue
          ).length;
          const canReset = resetFields
            ? schemaDefaultsToApply(resetFields, state.values).length > 0
            : false;
          return [
            state.canSubmit,
            state.isSubmitting,
            state.isDefaultValue,
            changeCount,
            canReset,
          ] as const;
        }}
        children={([
          canSubmit,
          isSubmitting,
          isDefaultValue,
          changeCount,
          canReset,
        ]) => (
          <div className="flex items-center gap-4">
            {resetFields && (
              <SystemSettingsFooterReset
                {...{ form, resetFields, canReset, isSubmitting }}
              />
            )}
            {changeCount > 0 && (
              <span className="text-sm text-text-secondary">
                {changeCount} Unsaved changes
              </span>
            )}
            <Button
              variant="contained"
              type="submit"
              disabled={!canSubmit || isSubmitting || isDefaultValue}
              data-test-id="system-settings__submit-button"
              data-ph-capture-attribute-action="submit_settings"
            >
              {isSubmitting ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      />
    </footer>
  );
}
